import AutoFixHighRounded from '@mui/icons-material/AutoFixHighRounded';
import ClearAllRounded from '@mui/icons-material/ClearAllRounded';
import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  InputAdornment,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { money } from '../../../lib/format';
import { businessDate } from '../../../lib/format';
import { useShop } from '../../../shop/ShopProvider';
import {
  fifoOrder,
  fromUnits,
  summarize,
  toUnits,
  type AllocationDraft,
  type OpenItem,
} from '../allocation';
import { docType } from './labels';

export interface EditorItem extends OpenItem {
  amount: string;
  dueDate: string | null;
  overdue: boolean;
}

/**
 * The allocation step: a party's open documents, oldest first, each with an amount to settle; an
 * "oldest first" shortcut, and a live summary of what is allocated and what stays as credit.
 */
export function AllocationEditor({
  amount,
  items,
  draft,
  loading,
  autoActive,
  onEdit,
  onAuto,
  onClear,
  creditLabel = 'Stays as credit',
  emptyText = 'Nothing is open for this party — the whole amount stays as credit.',
}: {
  amount: string;
  items: readonly EditorItem[];
  draft: AllocationDraft;
  loading?: boolean;
  autoActive?: boolean;
  onEdit: (id: string, value: string) => void;
  onAuto: () => void;
  onClear: () => void;
  creditLabel?: string;
  emptyText?: string;
}) {
  const { format } = useShop();
  const d = format.currencyDecimals;
  const s = summarize(amount, draft, items);
  const ordered = fifoOrder(items);

  const toggle = (item: EditorItem) => {
    const current = toUnits(draft[item.id] ?? '') ?? 0n;
    if (current > 0n) {
      onEdit(item.id, '');
      return;
    }
    const room = s.remaining > 0n ? s.remaining : 0n;
    const open = toUnits(item.open) ?? 0n;
    const take = room < open ? room : open;
    onEdit(item.id, take > 0n ? fromUnits(take, d) : '');
  };

  return (
    <Box>
      <Stack
        direction="row"
        spacing={1}
        sx={{ px: 2.5, py: 1.5, alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}
      >
        <Button
          size="small"
          variant={autoActive ? 'contained' : 'outlined'}
          startIcon={<AutoFixHighRounded />}
          onClick={onAuto}
          disabled={!items.length}
        >
          Oldest first
        </Button>
        <Button
          size="small"
          startIcon={<ClearAllRounded />}
          onClick={onClear}
          disabled={!items.length}
          sx={{ color: 'text.secondary' }}
        >
          Clear
        </Button>
        <Box sx={{ flex: 1 }} />
        {autoActive && (
          <Typography variant="caption" color="text.secondary">
            Automatic — follows the amount
          </Typography>
        )}
      </Stack>

      <TableContainer sx={{ borderTop: 1, borderColor: 'divider' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>Document</TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Due</TableCell>
              <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                Total
              </TableCell>
              <TableCell align="right">Open</TableCell>
              <TableCell align="right" sx={{ width: { xs: 120, sm: 160 } }}>
                Settle
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading &&
              [0, 1, 2].map((i) => (
                <TableRow key={i}>
                  <TableCell colSpan={6}>
                    <Skeleton height={36} />
                  </TableCell>
                </TableRow>
              ))}
            {!loading &&
              ordered.map((item) => {
                const value = draft[item.id] ?? '';
                const checked = (toUnits(value) ?? 0n) > 0n;
                const error = s.errors[item.id];
                return (
                  <TableRow
                    key={item.id}
                    selected={checked}
                    sx={{ '&:last-child td': { borderBottom: 0 } }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={checked}
                        onChange={() => toggle(item)}
                        slotProps={{ input: { 'aria-label': `Settle ${item.number ?? ''}` } }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {item.number ?? docType(item.type)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {docType(item.type)} · {businessDate(item.date, format)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      {item.overdue ? (
                        <Chip
                          size="small"
                          color="error"
                          variant="outlined"
                          label={`Overdue · ${businessDate(item.dueDate, format)}`}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {businessDate(item.dueDate, format)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell
                      align="right"
                      className="tabular"
                      sx={{ display: { xs: 'none', md: 'table-cell' }, color: 'text.secondary' }}
                    >
                      {money(item.amount, format)}
                    </TableCell>
                    <TableCell align="right" className="tabular" sx={{ fontWeight: 600 }}>
                      {money(item.open, format)}
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        value={value}
                        placeholder="0"
                        onChange={(e) => onEdit(item.id, e.target.value)}
                        error={Boolean(error)}
                        helperText={error}
                        slotProps={{
                          htmlInput: {
                            inputMode: 'decimal',
                            'aria-label': `Amount for ${item.number ?? docType(item.type)}`,
                            style: { textAlign: 'right' },
                            className: 'tabular',
                          },
                          input: {
                            startAdornment: (
                              <InputAdornment position="start" sx={{ mr: 0.25 }}>
                                <Typography variant="caption" color="text.disabled">
                                  {format.currencyCode}
                                </Typography>
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      {!loading && items.length === 0 && (
        <Stack spacing={1} sx={{ alignItems: 'center', py: 5, px: 3, textAlign: 'center' }}>
          <ReceiptLongRounded sx={{ color: 'text.disabled', fontSize: 32 }} />
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360 }}>
            {emptyText}
          </Typography>
        </Stack>
      )}

      <AllocationTotals
        amount={fromUnits(s.amount, d)}
        allocated={fromUnits(s.allocated, d)}
        remaining={fromUnits(s.remaining, d)}
        over={s.overAllocated}
        creditLabel={creditLabel}
      />
    </Box>
  );
}

export function AllocationTotals({
  amount,
  allocated,
  remaining,
  over,
  creditLabel,
  amountLabel = 'Amount',
}: {
  amount: string;
  allocated: string;
  remaining: string;
  over: boolean;
  creditLabel: string;
  amountLabel?: string;
}) {
  const { format } = useShop();
  const cells = [
    { label: amountLabel, value: money(amount, format), tone: 'text.primary' },
    { label: 'Allocated', value: money(allocated, format), tone: 'success.main' },
    over
      ? {
          label: 'Over-allocated',
          value: money(remaining.replace('-', ''), format),
          tone: 'error.main',
        }
      : {
          label: creditLabel,
          value: money(remaining, format),
          tone: Number(remaining) > 0 ? 'warning.main' : 'text.secondary',
        },
  ];
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        borderTop: 1,
        borderColor: 'divider',
        bgcolor: 'color-mix(in srgb, var(--mui-palette-primary-main) 3%, transparent)',
      }}
    >
      {cells.map((c, i) => (
        <Box
          key={c.label}
          sx={{ px: { xs: 1.5, sm: 2.5 }, py: 1.75, borderLeft: i ? 1 : 0, borderColor: 'divider' }}
        >
          <Typography variant="caption" color="text.secondary" component="div" noWrap>
            {c.label}
          </Typography>
          <Typography
            className="tabular"
            sx={{ fontWeight: 700, color: c.tone, fontSize: { xs: '0.875rem', sm: '1.0625rem' } }}
            role={c.label === 'Over-allocated' ? 'alert' : undefined}
          >
            {c.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}
